"use client";

import * as X from "@/ui";
import { menu, useRoute, type MenuSection } from "./common";
import { LocaleSelectorDesktop } from "./locale";

import ZapptaxDefaultLogo from "@/ui/shared/assets/zapptax-default.svg";
import ZapptaxRetailLogo from "@/ui/shared/assets/zapptax-retail.svg";
import ZapptaxShippingLogo from "@/ui/shared/assets/zapptax-shipping.svg";

type Pixels = number & {};


const MenuPaddingLeft: Pixels = 120;

/**
 * How long to wait before closing the menu when un-hovered.
 */
const MenuCloseDelay: X.Millis | null = 0;

/**
 * how long to keep the menu non-openable after having clicked it, to force it
 * stay closed after having clicked on it.
 */
const MenuCooldown: X.Millis | null = 150;


export function MenuDesktop({
  withHeroImage,
  isScrolled,
  ...pageProps
}: X.PageProps<{
  withHeroImage?: boolean;
  isScrolled?: boolean;
}>) {
  const menuBarRef = X.useRef<HTMLDivElement>(null);
  const [dropdownStyle, setDropdownStyle] = X.useState<React.CSSProperties>({});
  const [section, setSection] = X.useState<MenuSection>("travelers");
  const [isOpen, setIsOpen] = X.useState<boolean>(false);

  const recentlyClosedRef = X.useRef(false);
  const closeMenu = () => {
    setIsOpen(false);

    if (MenuCooldown !== null) {
      recentlyClosedRef.current = true;
      setTimeout(() => {
        recentlyClosedRef.current = false;
      }, MenuCooldown);
    }
  };

  useCloseOnPathChange(closeMenu);

  const closeTimer = X.useRef<NodeJS.Timeout>(null);

  const route = useRoute();
  const logo = (
    route[0] === "shipping" ? { image: ZapptaxShippingLogo, height: "h-16", alt: "Zapptax Shipping", to: "/shipping" as X.PageHref } :
    route[0] === "retail" ? { image: ZapptaxRetailLogo, height: "h-16", alt: "Zapptax Retail", to: "/retail" as X.PageHref } :
    { image: ZapptaxDefaultLogo, height: "h-10", alt: "Zapptax", to: "/" as X.PageHref }
  );

  X.useEffect([isOpen], () => {
    if (closeTimer.current) {
      clearTimeout(closeTimer.current);
    }

    if (!isOpen) {
      if (MenuCloseDelay === null) {
        setDropdownStyle({});
        return;
      }

      closeTimer.current = setTimeout(() => setDropdownStyle({}), MenuCloseDelay);
      return;
    }

    if (isOpen && menuBarRef.current) {
      const rect = menuBarRef.current.getBoundingClientRect();
      const left = Math.max(0, rect.left - MenuPaddingLeft);
      const top = Math.max(0, rect.top);

      setDropdownStyle({
        display: "flex",
        position: "fixed",
        top: 0,
        right: 0,
        left,
        //zIndex: 50,
        paddingLeft: MenuPaddingLeft,
        paddingTop: top,
        // TODO animate
        //transform: `translateY(-100%)`,
      });
    }
  });

  return (
    <>
      <header className={X.clsx(
        "hidden xl:block px-6 transition-colors duration-300 h-header-desktop",
        isScrolled ? "bg-white/100" : withHeroImage ? "bg-white/0" : "bg-white/100",
        "fixed top-0 right-0 left-0 z-30"
      )}>
        <div className={X.clsx("max-w-content mx-auto flex justify-between items-center h-header-desktop")}>
          <X.Link href={logo.to} className="flex items-center">
            <X.Image
              src={logo.image}
              alt={logo.alt}
              className={`w-auto ${logo.height}`}
            />
          </X.Link>

          <nav onMouseLeave={closeMenu}>
            <MenuBar
              {...pageProps}
              ref={menuBarRef}
              setIsOpen={setIsOpen}
              recentlyClosedRef={recentlyClosedRef}
              setSection={setSection}
            />

            <MenuOverlay
              {...pageProps}
              style={dropdownStyle}
              setIsOpen={setIsOpen}
              isOpen={isOpen}
              recentlyClosedRef={recentlyClosedRef}
              setSection={setSection}
              section={section}
            />
          </nav>
        </div>
      </header>

      {!withHeroImage && (
        <div className={X.clsx(
          "hidden xl:block h-header-desktop",
        )}/>
      )}
    </>
  );
}


function MenuOverlay({
  style,
  setIsOpen,
  recentlyClosedRef,
  setSection,
  section,
  isOpen,
  ...pageProps
}: X.PageProps<{
  style?: React.CSSProperties;
  setIsOpen: (isOpen: boolean) => void;
  recentlyClosedRef?: React.RefObject<boolean>;
  setSection: (s: MenuSection) => void;
  section: MenuSection;
  isOpen: boolean;
}>) {
  const tr = X.useTranslator(pageProps);
  const close = () => setIsOpen(false);

  return (
    <div
      className={X.clsx("hidden z-50 rounded-bl-[36px] flex-col bg-white shadow-[0_0_6px_3px_rgba(0,0,0,0.1)]")}
      style={style}
    >
      <MenuBar
        {...pageProps}
        section={section}
        setIsOpen={setIsOpen}
        recentlyClosedRef={recentlyClosedRef}
        setSection={setSection}
      />
      <div className={X.clsx("flex flex-col px-5 py-10 gap-6 pb-20")}>
        {Object.entries(menu[section]["sub"]).filter(([_, sub]) => sub.type !== "large").map(([key, sub]) => (
          sub.type === "button" ? (
            <div key={key} className="flex flex-row justify-start">
              <X.Button
                label={tr(sub.label) as string}
                secondary
                exitHref={sub.exitHref as string}
                onClick={close}
                href={sub.href as never}
              />
            </div>
          ) : (
            <X.Link key={key} href={sub.href} onClick={close} className={X.clsx(
              "flex flex-row gap-2 items-center",
              sub.type === "large" ? "text-px20" : "text-px-14",
              "hover:font-light hover:[&_strong]:font-normal group",
            )}>
              <p>{tr(sub.label)}</p>
              <div className={X.clsx(
                "group-hover:translate-x-2 transition-transform transform-gpu",
              )}>
                {sub.type === "large" ? <X.Icon.MoveRight /> : <X.Icon.ChevronRight />}
              </div>
            </X.Link>
          )
        ))}
      </div>
    </div>
  );
}


function MenuBar({
  ref,
  section: sectionName,
  setSection,
  setIsOpen,
  recentlyClosedRef,
  ...pageProps
}: X.PageProps<{
  ref?: React.RefObject<HTMLDivElement | null>;
  section?: MenuSection;
  setSection: (s: MenuSection) => void;
  setIsOpen: (isOpen: boolean) => void;
  recentlyClosedRef?: React.RefObject<boolean>;
}>) {
  const route = useRoute();
  const tr = X.useTranslator(pageProps);
  const isRetail = route[0] === "retail";

  return (
    <div ref={ref} className="flex flex-row items-center">
      {Object.entries(menu).filter(([_, section]) => !section.onlyFooter).map(([name, section]) => (
        <X.Link
          key={name}
          href={section.href}
          onMouseEnter={(e) => {
            const hasSubmenu = Object.keys(section.sub).length > 0;

            if (!recentlyClosedRef?.current) {
              setSection?.(name as MenuSection);
              setIsOpen?.(hasSubmenu);

              if (!hasSubmenu) {
                const target = e.currentTarget; // <-- capture it NOW

                setTimeout(() => {
                  if (target?.isConnected) { // <- safe guard: only dispatch if still in DOM
                    target.dispatchEvent(new MouseEvent("mousemove", { bubbles: true }));
                  }
                }, MenuCloseDelay ?? 0);
              }
            }
          }}
          className={X.clsx(
            "hover:bg-yellow",
            sectionName === name ? "bg-yellow": "",
            "py-3 px-5 rounded-[30px] text-px15 text-nowrap",
            "cursor-pointer",
            (section.match?.(route) ?? (route[0] === name)) && "font-semibold",
          )}
        >
          {tr(section.label)}
        </X.Link>
      ))}
      <LocaleSelectorDesktop
        {...pageProps}
        hoverColor="black"
        className="ml-6"
      />
      {isRetail && (
        <X.Button
          secondary
          className="ml-10"

          //exitHref={X.links.login}
          disabled
          tooltip={tr({
            en: "Coming soon",
            fr: "Bientôt",
          })}

          label={tr({
            en: "Sign in",
            fr: "Se connecter",
          })}
        />
      )}
    </div>
  );
}


export function useCloseOnPathChange(onClose: () => void) {
  const pathname = X.usePathname();

  X.useEffect([pathname], () => {
    onClose();
  });
}
